import { DEFAULT_RANGE_MIN, DEFAULT_RANGE_MAX, DIAL_MIN_ID, DIAL_MAX_ID } from './index';
import queryStringUtils from 'query-string';

export const constrainToMinRange = (val) => Math.max(DEFAULT_RANGE_MIN, val);

export const constrainToMaxRange = (val) => Math.min(DEFAULT_RANGE_MAX, val);

export const roundToStep = (input, step = 1) => Math.round(input / step) * step;

export const roundToLowerStep = (input, step = 1) => {
    if (input === DEFAULT_RANGE_MAX) {
        return DEFAULT_RANGE_MAX - step;
    }
    const rounded = Math.floor(input / step) * step;
    return constrainToMinRange(roundToStep(rounded, step));
};

export const contrainToPercentRange = (val) => Math.max(0, Math.min(100, val));

export const roundToUpperStep = (input, step = 1) => {
    if (input === DEFAULT_RANGE_MIN) {
        return step;
    }
    const rounded = Math.floor(input / step) * step;
    const remainder = input % step === 0 ? 0 : step;
    return constrainToMaxRange(rounded + remainder);
};

const memoizeRatios = () => {
    const ratios = {};

    return (domain) => {
        const [domainMin, domainMax] = domain;
        const id = domainMax - domainMin;
        if (ratios[id]) {
            return ratios[id];
        }

        const ratio = id / 100;
        ratios[id] = ratio;

        return ratio;
    };
};

const memoizeValuesAsPercent = () => {
    const values = {};

    return (targetDomainVal, domain) => {
        if (values[targetDomainVal]) {
            return values[targetDomainVal];
        }
        const [domainMin] = domain;
        const ratio = getPercentToDomainRatio(domain);
        const valueAsPercent = (targetDomainVal - domainMin) / ratio;
        values[targetDomainVal] = valueAsPercent;

        return valueAsPercent;
    };
};

// Percent <-> Value transformations

const getPercentToDomainRatio = memoizeRatios();

const valueToPercent = memoizeValuesAsPercent();

export const percentToDomainValue = (targetPercent, domain) => {
    if (!Array.isArray(domain) || domain.length !== 2) {
        throw new Error(`percentToDomainValue expect a domain with exact length 2 but got: ${JSON.stringify(domain)}`);
    }
    const [domainMin] = domain;
    const ratio = getPercentToDomainRatio(domain);
    return Math.round(targetPercent * ratio + domainMin);
};

export const domainValueToPercent = (targetDomainVal, domain) => {
    if (!Array.isArray(domain) || domain.length !== 2) {
        throw new Error(`domainValueToPercent expect a domain with exact length 2 but got: ${JSON.stringify(domain)}`);
    }
    return valueToPercent(targetDomainVal, domain);
};

export const calculateNewRange = ({ currentDial, domain = [0, 100], rangeAsPercent, targetPercent: rawTargetPercent, step = 1 }) => {
    // we assume that range as percent has valid step percent equivalents
    const [currMinPercent, currMaxPercent] = rangeAsPercent;
    const distanceFromMin = rawTargetPercent - currMinPercent;
    const distanceFromMax = rawTargetPercent - currMaxPercent;
    const absDistanceFromMin = Math.abs(distanceFromMin);
    const absDistanceFromMax = Math.abs(distanceFromMax);

    const targetIsEquidistant = absDistanceFromMin === absDistanceFromMax;
    if (targetIsEquidistant) {
        return rangeAsPercent;
    }

    // ensure target percent maps to valid domain value
    const rawTargetDomainVal = percentToDomainValue(rawTargetPercent, domain);
    let roundedPercent = contrainToPercentRange(domainValueToPercent(roundToStep(rawTargetDomainVal, step), domain));

    // targetPercent is less than currMinPercent
    if (roundedPercent <= currMinPercent) {
        if (currentDial === DIAL_MAX_ID) {
            // return rangeAsPercent;
            return [currMinPercent, contrainToPercentRange(currMinPercent + domainValueToPercent(step, domain))];
        }
        return [roundedPercent, currMaxPercent];
    }

    // targetPercent is between min and max
    if (currMinPercent < roundedPercent && roundedPercent < currMaxPercent) {
        if (currentDial === DIAL_MIN_ID) {
            return [roundedPercent, currMaxPercent];
        }
        if (currentDial === DIAL_MAX_ID) {
            return [currMinPercent, roundedPercent];
        }
        return absDistanceFromMin < absDistanceFromMax ? [roundedPercent, currMaxPercent] : [currMinPercent, roundedPercent];
    }

    // targetPercent is greater than currMaxPercent
    if (currMaxPercent <= roundedPercent) {
        if (currentDial === DIAL_MIN_ID) {
            return [contrainToPercentRange(currMaxPercent - domainValueToPercent(step, domain)), currMaxPercent];
        }
        return [currMinPercent, roundedPercent];
    }

    return rangeAsPercent;
};

export const rangeAsValueToPercent = (range, domain) => {
    const [currentMinValue, currentMaxValue] = range;
    return [domainValueToPercent(currentMinValue, domain), domainValueToPercent(currentMaxValue, domain)];
};

export function simpleThrottle(fn, delay) {
    let lastCall = 0;
    return function (...args) {
        const now = new Date().getTime();
        if (now - lastCall < delay) {
            return;
        }
        lastCall = now;
        return fn(...args);
    };
}

export function isPriceReset() {
    const { minPrice, maxPrice } = queryStringUtils.parse(location.search);
    return !minPrice && !maxPrice;
}
